import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { settingsApi } from '../utils/api';

/**
 * GoogleAnalytics Component
 * 
 * Automatically loads and initializes Google Analytics based on the
 * Measurement ID stored in site settings. Tracks page views on route changes.
 * 
 * Features:
 * - Fetches GA ID from database on mount
 * - Dynamically injects gtag.js script
 * - Tracks page views on every route change
 * - Respects enable_analytics setting
 * - Gracefully handles missing/invalid GA ID
 */
export default function GoogleAnalytics() {
  const location = useLocation();
  const [gaId, setGaId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Fetch GA settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { settings } = await settingsApi.getPublic();
        
        // Check if analytics is enabled
        if (settings.enable_analytics === 'false') {
          console.log('[GA] Analytics disabled in settings');
          return;
        }

        const id = settings.google_analytics_id;
        if (id && id.startsWith('G-')) {
          setGaId(id);
        } else {
          console.log('[GA] No valid Google Analytics ID configured');
        }
      } catch (error) {
        console.error('[GA] Failed to load settings:', error);
      }
    };

    loadSettings();
  }, []);

  // Initialize GA when we have an ID
  useEffect(() => {
    if (!gaId || initialized) return;

    // Check if already loaded (prevents duplicate scripts)
    if (window.gtag) {
      setInitialized(true);
      return;
    }

    // Create and inject the gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    
    script.onload = () => {
      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      
      // Set initial config
      window.gtag('js', new Date());
      window.gtag('config', gaId, {
        page_path: location.pathname + location.search,
        send_page_view: true
      });

      setInitialized(true);
      console.log(`[GA] Initialized with ID: ${gaId}`);
    };

    script.onerror = () => {
      console.error('[GA] Failed to load gtag.js');
    };

    document.head.appendChild(script);

    // Cleanup on unmount (though typically this component stays mounted)
    return () => {
      // Don't remove script - it should persist
    };
  }, [gaId, initialized, location]);

  // Track page views on route change
  useEffect(() => {
    if (!initialized || !gaId || !window.gtag) return;

    // Send page view
    window.gtag('config', gaId, {
      page_path: location.pathname + location.search,
      page_title: document.title
    });

    console.log(`[GA] Page view: ${location.pathname}`);
  }, [location, initialized, gaId]);

  // This component doesn't render anything
  return null;
}

/**
 * Helper function to track custom events
 * Can be imported and used anywhere in the app
 * 
 * Usage:
 * import { trackEvent } from '../components/GoogleAnalytics';
 * trackEvent('button_click', { button_name: 'start_game' });
 */
export function trackEvent(eventName, eventParams = {}) {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
    console.log(`[GA] Event: ${eventName}`, eventParams);
  }
}

/**
 * Track game-specific events
 * Useful for measuring engagement with games
 */
export const gameAnalytics = {
  // Track when a game starts
  gameStart: (gameId, level = 1) => {
    trackEvent('game_start', {
      game_id: gameId,
      level: level
    });
  },

  // Track level completion
  levelComplete: (gameId, level, score = null, timeSeconds = null) => {
    trackEvent('level_complete', {
      game_id: gameId,
      level: level,
      score: score,
      time_seconds: timeSeconds
    });
  },

  // Track word practiced successfully
  wordPracticed: (gameId, word, attempts = 1) => {
    trackEvent('word_practiced', {
      game_id: gameId,
      word: word,
      attempts: attempts
    });
  },

  // Track session duration
  sessionEnd: (gameId, durationSeconds, levelsPlayed = []) => {
    trackEvent('game_session_end', {
      game_id: gameId,
      duration_seconds: durationSeconds,
      levels_played: levelsPlayed.length
    });
  }
};
